/**
 * 커미션 관련 Write 작업 목차
 * 
 * - createCommission() - 커미션 생성
 * - updateCommission() - 커미션 수정
 * - deleteCommission() - 커미션 삭제
 * - setCommissionApproval() - 승인 설정 (관리자)
 * - toggleCommissionApproval() - 승인 토글 (관리자)
 */

import { collection, addDoc, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { ref, uploadBytes, deleteObject } from "firebase/storage";
import { firestore, storage } from "/firebaseConfig";

/**
 * 커미션 생성
 * @param {Object} commissionData - 커미션 데이터 (status: 'draft' | 'published' 선택)
 * @param {File} thumbnailFile - 썸네일 이미지 파일
 * @returns {Promise<{ id: string, thumbnail: string }>} 생성된 문서 id 및 저장된 썸네일 파일명
 */
export async function createCommission(commissionData, thumbnailFile) {
  try {
    let thumbnailFileName = commissionData.thumbnail || "";
    
    // 썸네일 파일이 있으면 Storage에 업로드
    if (thumbnailFile) {
      // 파일명 생성 (타임스탬프 + 원본 파일명)
      const timestamp = Date.now();
      const fileExtension = thumbnailFile.name.split('.').pop();
      thumbnailFileName = `commission_${timestamp}.${fileExtension}`;
      
      // Storage에 업로드
      const storageRef = ref(storage, `commission/${thumbnailFileName}`);
      await uploadBytes(storageRef, thumbnailFile);
    }

    // 세부 이미지 업로드
    const detailImageNames = [];
    if (Array.isArray(commissionData._detailImageFiles)) {
      for (let i = 0; i < commissionData._detailImageFiles.length; i++) {
        const f = commissionData._detailImageFiles[i];
        if (!f) { detailImageNames.push(""); continue; }
        const ts = Date.now();
        const ext = f.name.split(".").pop();
        const fname = `commission_detail${i + 1}_${ts}.${ext}`;
        const sRef = ref(storage, `commission/${fname}`);
        await uploadBytes(sRef, f);
        detailImageNames.push(fname);
      }
    }

    // Firestore에 커미션 데이터 저장
    const commissionDoc = {
      category: commissionData.category,
      name: commissionData.name,
      link: commissionData.link,
      description: commissionData.description || "",
      descriptionHtml: commissionData.descriptionHtml || "",
      thumbnail: thumbnailFileName,
      hashtags: Array.isArray(commissionData.hashtags) ? commissionData.hashtags : [],
      detailImages: detailImageNames.length > 0 ? detailImageNames : (commissionData.detailImages || []),
      youtubeVideos: commissionData.youtubeVideos || [],
      purchaseType: commissionData.purchaseType || "",
      basePrice: Number(commissionData.basePrice) || 0,
      purchaseLink: commissionData.purchaseLink || "",
      settlementBank: commissionData.settlementBank || "",
      settlementAccountNumber: commissionData.settlementAccountNumber || "",
      settlementAccountHolder: commissionData.settlementAccountHolder || "",
      sellerName: commissionData.sellerName || "",
      createdBy: commissionData.createdBy,
      createdByNickname: commissionData.createdByNickname,
      createdAt: commissionData.createdAt,
      status: commissionData.status || "published",
      /** 신규 게시는 승인 대기(false). 기존 데이터 호환용으로 명시 true/false만 저장 */
      approved: typeof commissionData.approved === "boolean" ? commissionData.approved : false,
    };

    const docRef = await addDoc(collection(firestore, "commissions"), commissionDoc);
    return { id: docRef.id, thumbnail: thumbnailFileName };
  } catch (error) {
    console.error("커미션 생성 오류:", error);
    throw error;
  }
}

/**
 * 커미션 수정
 * @param {string} commissionId - 커미션 ID
 * @param {Object} updateData - 수정할 데이터
 * @param {File} thumbnailFile - 썸네일 이미지 파일 (선택)
 * @returns {Promise<void>}
 */
export async function updateCommission(commissionId, updateData, thumbnailFile) {
  try {
    let thumbnailFileName = updateData.thumbnail || "";
    
    // 새 썸네일 파일이 있으면 Storage에 업로드
    if (thumbnailFile) {
      // 기존 썸네일 삭제 (있는 경우)
      if (updateData.thumbnail && !updateData.thumbnail.startsWith("http")) {
        try {
          const oldStorageRef = ref(storage, `commission/${updateData.thumbnail}`);
          await deleteObject(oldStorageRef);
        } catch (error) {
          console.warn("기존 썸네일 삭제 실패 (무시):", error);
        }
      }
      
      // 새 파일명 생성
      const timestamp = Date.now();
      const fileExtension = thumbnailFile.name.split('.').pop();
      thumbnailFileName = `commission_${timestamp}.${fileExtension}`;
      
      // Storage에 업로드
      const storageRef = ref(storage, `commission/${thumbnailFileName}`);
      await uploadBytes(storageRef, thumbnailFile);
    }

    // 세부 이미지 업로드
    const detailImageNames = [...(updateData.detailImages || [])];
    if (Array.isArray(updateData._detailImageFiles)) {
      for (let i = 0; i < updateData._detailImageFiles.length; i++) {
        const f = updateData._detailImageFiles[i];
        if (!f) continue;
        const ts = Date.now();
        const ext = f.name.split(".").pop();
        const fname = `commission_detail${i + 1}_${ts}.${ext}`;
        const sRef = ref(storage, `commission/${fname}`);
        await uploadBytes(sRef, f);
        detailImageNames[i] = fname;
      }
    }

    // Firestore 업데이트
    const updateDocData = {
      category: updateData.category,
      name: updateData.name,
      link: updateData.link,
      description: updateData.description || "",
      descriptionHtml: updateData.descriptionHtml || "",
      thumbnail: thumbnailFileName,
      hashtags: Array.isArray(updateData.hashtags) ? updateData.hashtags : [],
      detailImages: detailImageNames,
      youtubeVideos: updateData.youtubeVideos || [],
      purchaseType: updateData.purchaseType || "",
      basePrice: Number(updateData.basePrice) || 0,
      purchaseLink: updateData.purchaseLink || "",
      settlementBank: updateData.settlementBank || "",
      settlementAccountNumber: updateData.settlementAccountNumber || "",
      settlementAccountHolder: updateData.settlementAccountHolder || "",
      sellerName: updateData.sellerName || "",
      lastModifiedBy: updateData.lastModifiedBy,
      lastModifiedByNickname: updateData.lastModifiedByNickname,
      lastModifiedAt: updateData.lastModifiedAt,
      status: updateData.status ?? "published",
    };

    if (Object.prototype.hasOwnProperty.call(updateData, "approved")) {
      updateDocData.approved = updateData.approved;
    }

    await updateDoc(doc(firestore, "commissions", commissionId), updateDocData);
  } catch (error) {
    console.error("커미션 수정 오류:", error);
    throw error;
  }
}

/**
 * 커미션 삭제
 * @param {string} commissionId - 커미션 ID
 * @param {string|Object|null} thumbnailOrCommission - 썸네일 파일명 문자열, 또는 { thumbnail, detailImages } 객체
 * @returns {Promise<void>}
 */
export async function deleteCommission(commissionId, thumbnailOrCommission = null) {
  let thumbnailFileName = null;
  let detailImageNames = [];
  if (thumbnailOrCommission && typeof thumbnailOrCommission === "object") {
    thumbnailFileName = thumbnailOrCommission.thumbnail || null;
    detailImageNames = Array.isArray(thumbnailOrCommission.detailImages)
      ? thumbnailOrCommission.detailImages
      : [];
  } else if (thumbnailOrCommission) {
    thumbnailFileName = thumbnailOrCommission;
  }

  try {
    for (const fname of detailImageNames) {
      if (!fname || String(fname).startsWith("http")) continue;
      try {
        await deleteObject(ref(storage, `commission/${fname}`));
      } catch (error) {
        console.warn("세부 이미지 삭제 실패 (무시):", fname, error);
      }
    }

    if (thumbnailFileName && !String(thumbnailFileName).startsWith("http")) {
      try {
        const storageRef = ref(storage, `commission/${thumbnailFileName}`);
        await deleteObject(storageRef);
      } catch (error) {
        console.warn("썸네일 삭제 실패 (무시):", error);
      }
    }

    await deleteDoc(doc(firestore, "commissions", commissionId));
  } catch (error) {
    console.error("커미션 삭제 오류:", error);
    throw error;
  }
}

/**
 * 커미션 승인 여부 설정 (관리자 UI용)
 * @param {string} commissionId
 * @param {boolean} approved
 */
export async function setCommissionApproval(commissionId, approved) {
  await updateDoc(doc(firestore, "commissions", commissionId), { approved: !!approved });
}

/**
 * 커미션 승인 토글
 * @param {string} commissionId
 * @param {boolean} currentApproved - LiveDetail과 동일하게 `|| false`로 넘기면 undefined는 미승인 취급
 * @returns {Promise<boolean>} 변경 후 승인 여부
 */
export async function toggleCommissionApproval(commissionId, currentApproved) {
  const next = !currentApproved;
  await setCommissionApproval(commissionId, next);
  return next;
}
